/**
 * The Givrecoeur Company (ADR 0067, docs/LORE.md): its quarter at Havrebourg,
 * where Ser Givrecoeur stands for everyone, and the recruits the player
 * gathers, one per region, who join him there for their recruiter alone.
 *
 * Plain data, no import of the bestiary: `mobs.ts` reads this to add the
 * recruits' quarter copies.
 */
/** The quarter's name among the world's landmarks, and the place a quest may send one to. */
export const COMPANY_QUARTER = 'Quartier de la Compagnie';
/**
 * Each region's recruit quest → the townsperson it recruits (the ADR's
 * contract). The regional copy stays where it is, for everyone's quests;
 * the recruit at the quarter is its own creature (`companyMobOf`), so no
 * quest of the region is given nor taken back there: only the Company's
 * finale (ADR 0068, `quests/company-finale.ts`) speaks to them.
 */
export const COMPANY_RECRUITS = {
    'meadow-recruit': 'lookout',
    'forest-recruit': 'forest-druid',
    'swamp-recruit': 'swamp-hunter',
    'desert-recruit': 'jackal-priestess',
    'snow-recruit': 'snow-warden',
};
/** The recruits' quarter copies, in the order of `COMPANY_RECRUITS`. */
export const COMPANY_MOB_IDS = [
    'company-lookout',
    'company-forest-druid',
    'company-swamp-hunter',
    'company-jackal-priestess',
    'company-snow-warden',
];
/** The creature that stands at the quarter for a recruited townsperson. */
export function companyMobOf(mob) {
    return `company-${mob}`;
}
/** The recruit quests of the list, in the order of `COMPANY_RECRUITS`. */
export const RECRUIT_QUEST_IDS = Object.keys(COMPANY_RECRUITS);
export function isRecruitQuest(questId) {
    return Object.hasOwn(COMPANY_RECRUITS, questId);
}
/**
 * What a recruit says at the quarter when spoken to. Placeholders in the
 * tone of the lore, epic and serious; the writers of the chains may refine them.
 */
export const COMPANY_GREETINGS = {
    'company-lookout': 'D’ici, je vois ma vieille tour. Le nouveau guetteur dort à son poste, je le vois très bien !',
    'company-forest-druid': 'Il n’y a pas un seul arbre dans ce quartier ! Je vais en planter un. Ser Givrecoeur a dit oui. Enfin, je crois.',
    'company-swamp-hunter': 'Trois cordes, deux harpons et un filet. Il m’en faudrait le double pour un dragon !',
    'company-jackal-priestess': 'J’ai allumé un brasero dans la cour. Si je m’absente, ne le laisse surtout pas s’éteindre !',
    'company-snow-warden': 'Givrecoeur ronfle. Il ronflait déjà quand on était petits, ça ne s’est pas arrangé.',
};
/** What Ser Givrecoeur says at the quarter with nothing to give nor take (the camp has its own). */
export const COMPANY_TUTOR_GREETING = 'Ramène-moi des gens courageux, recrue. Des gens qui n’ont pas peur du froid !';
/**
 * The Company's last stand (ADR 0068 §4): the quest whose stand, once made,
 * leaves the fallen behind for its player. Also `LAST_STAND_QUEST_ID` of
 * `shefira.ts` (a test holds them equal); written here too so this file
 * stays plain data that the bestiary and the generator may read.
 */
export const COMPANY_LAST_STAND = 'company-last-stand';
/**
 * Who falls in Shefira's Aire, for the player who made the stand (ADR 0068
 * §4): Ser Givrecoeur and the recruits, in their regions and at the quarter,
 * hidden from that player alone (`goneAfter` of their spawns, stamped on the
 * world as it loads). Ulfar survives: his regional self and his seat at the
 * war table go too, for he now stands at Ser Givrecoeur's place, by the
 * memorial (`COMPANY_WARDEN`).
 */
export const COMPANY_FALLEN_MOBS = [
    'tutor',
    ...Object.values(COMPANY_RECRUITS),
    ...COMPANY_MOB_IDS,
];
/**
 * Who leaves the quarter for La Brèche once a player accepts the last stand
 * (ADR 0068 §4): Ser Givrecoeur and the five at the war table. For that
 * player they stand round the chasm instead (`appearsOn`), until they fall.
 */
export const COMPANY_MARCHERS = ['tutor', ...COMPANY_MOB_IDS];
/** Ulfar at the quarter after the stand, at Ser Givrecoeur's place: he keeps the memorial and the vow. */
export const COMPANY_WARDEN = 'company-warden';
/**
 * The memorial's steles (ADR 0068 §4), one for each of the five who fell,
 * by the one it stands for: inhabitants rather than props, so that only the
 * player who made the stand sees them. Each is named after its dead and
 * says its epitaph when clicked.
 */
export const MEMORIAL_STELES = {
    'stele-givrecoeur': 'tutor',
    'stele-maelle': 'lookout',
    'stele-melisande': 'forest-druid',
    'stele-nella': 'swamp-hunter',
    'stele-meret': 'jackal-priestess',
};
export const MEMORIAL_STELE_IDS = Object.keys(MEMORIAL_STELES);
//# sourceMappingURL=company.js.map